import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useDays, useTripFilters, useTripStore } from '@koob/trip-designer';

const blankDay = () => ({
  id: uuidv4(),
  experienceId: null,
  hotelId: null,
  experienceDayIndex: null,
  experiencePricing: null,
  hotelPricing: null,
  hotelBookedOnMyOwn: false,
  selectedExtras: [],
});

// First paint of the compose screen. Fills the store with blank days when the
// trip has none, and says when the first experience has settled (the loader
// covers the screen until then).
export default function useComposeMapBootstrap({
  tripId,
  hasHydrated,
  areProgramsLoading,
  experiences,
}) {
  const days = useDays();
  const tripFilters = useTripFilters();
  const experiencesMap = useTripStore(state => state.experiences);
  const hotelsMap = useTripStore(state => state.hotels);
  const updateDays = useTripStore(state => state.updateDays);
  const initializeTrip = useTripStore(state => state.initializeTrip);
  const [isFirstRender, setIsFirstRender] = useState(true);

  const isNotFirstExperience = useMemo(() => {
    return (
      (!areProgramsLoading && !experiences?.length) ||
      experiences?.[0] ||
      !isFirstRender
    );
  }, [areProgramsLoading, experiences, isFirstRender]);

  useEffect(() => {
    if (isNotFirstExperience && isFirstRender) {
      setIsFirstRender(false);
    }
  }, [isNotFirstExperience, isFirstRender]);

  // These three track whether ComposeScreen's own hydrate has loaded the
  // store at least once (with real days or the tripDuration-sized blank
  // ones): before that, `days`/`experiencesMap`/`hotelsMap` may still be
  // last session's persisted content or the zustand defaults, neither of
  // which this first-paint fallback should treat as "the trip has loaded".
  const totalTripDays = hasHydrated ? (days?.length ?? 0) : 0;
  const hasExperiences = hasHydrated;
  const hasHotels = hasHydrated;
  const tripDuration = tripFilters?.tripDuration;

  useEffect(() => {
    if (totalTripDays === 0 && (!days || days.length === 0)) {
      updateDays(Array.from({ length: tripDuration }, blankDay));
    }
    if (!hasExperiences && Object.keys(experiencesMap).length === 0) {
      initializeTrip({ days, experiences: {}, hotels: hotelsMap });
    }
    if (!hasHotels && Object.keys(hotelsMap).length === 0) {
      initializeTrip({ days, experiences: experiencesMap, hotels: {} });
    }
  }, [
    tripId,
    totalTripDays,
    hasExperiences,
    hasHotels,
    tripDuration,
    updateDays,
    initializeTrip,
  ]);

  return { isNotFirstExperience };
}
